import { TeamRequest, ApiResponse, TeamTemplate, AppSettings, Administrator, TeamPrivacyType, GraphUser } from "../types";
import { telemetryService } from "./telemetry.service";

// Chargement lazy de Teams JS pour éviter les erreurs hors de Teams
let teamsJs: typeof import("@microsoft/teams-js") | null = null;

/**
 * Obtient un token utilisateur Teams pour les flux OBO (On-Behalf-Of)
 * @returns Promise<string | null> - Le token utilisateur ou null en cas d'échec
 */
export async function getTeamsUserToken(): Promise<string | null> {
  try {
    if (!teamsJs) {
      teamsJs = await import("@microsoft/teams-js");
    }
    // Ne lève pas d'erreur si déjà initialisé
    try { await teamsJs.app.initialize(); } catch {}

    // 🔍 DIAGNOSTIC: Log Teams context to see what resource URI is configured
    try {
      const context = await teamsJs.app.getContext();
      console.log("[apiService] 🔍 Teams context for SSO:", {
        appSessionId: context.app?.host?.sessionId,
        frameContext: context.page?.frameContext,
        origin: window.location.origin,
        href: window.location.href,
        userTenantId: context.user?.tenant?.id,
        appId: context.app?.appLaunchId
      });
    } catch (ctxError) {
      console.warn("[apiService] Could not retrieve Teams context:", ctxError);
    }

    const token = await teamsJs.authentication.getAuthToken({ silent: true });
    console.log("[apiService] ✅ Teams SSO token retrieved successfully", {
      tokenPreview: token.substring(0, 20) + "...",
      tokenLength: token.length
    });
    return token;
  } catch (error: any) {
    console.error("[apiService] ❌ Failed to get Teams SSO token - FULL ERROR DETAILS:", {
      message: error.message,
      errorCode: error.errorCode,
      name: error.name,
      stack: error.stack,
      fullError: JSON.stringify(error, null, 2)
    });
    return null;
  }
}

// ✨ MULTI-TENANT: Get Azure Tenant ID from Teams context
async function getAzureTenantId(): Promise<string | null> {
  try {
    if (!teamsJs) {
      teamsJs = await import("@microsoft/teams-js");
    }
    // Ne lève pas d'erreur si déjà initialisé
    try { await teamsJs.app.initialize(); } catch {}
    const context = await teamsJs.app.getContext();
    return context.user?.tenant?.id || null;
  } catch {
    return null;
  }
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

// 🔍 DEBUG: Log the API base URL to diagnose configuration issues
console.log('[apiService] 🔍 API_BASE_URL loaded:', {
  value: API_BASE_URL,
  isDefined: !!import.meta.env.VITE_API_BASE_URL,
  environment: import.meta.env.MODE,
  timestamp: new Date().toISOString()
});

class ApiService {
  private validateApiConfig(): void {
    if (!API_BASE_URL || API_BASE_URL.trim() === '') {
      throw new Error(
        'API Configuration Error: VITE_API_BASE_URL is not configured.\n' +
        'Please set VITE_API_BASE_URL in your .env file or .localConfigs.\n' +
        'Example: http://localhost:3978/api/'
      );
    }
  }

  private async fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
    // Validate API configuration
    this.validateApiConfig();

    // ✨ MULTI-TENANT: Get Azure Tenant ID and add to headers
    const azureTenantId = await getAzureTenantId();

    const headers = {
      "Content-Type": "application/json",
      ...(azureTenantId ? { "X-Azure-Tenant-Id": azureTenantId } : {}),
      ...options.headers,
    };

    const method = options.method || "GET";
    const fullUrl = `${API_BASE_URL.replace(/\/$/, '')}${url}`;
    const startTime = performance.now();

    // 🔍 DEBUG: Log the full URL being called
    console.log('[apiService] 🔍 Calling API endpoint:', {
      method,
      endpoint: url,
      fullUrl,
      apiBaseUrl: API_BASE_URL,
      timestamp: new Date().toISOString()
    });

    try {
      const response = await fetch(fullUrl, {
        ...options,
        headers,
      });

      const duration = performance.now() - startTime;
      const statusCode = response.status;

      // 📊 Track API calls in telemetry
      if (telemetryService.isInitialized()) {
        telemetryService.capture(`api_call_${method.toLowerCase()}`, 'api-interceptor', {
          properties: {
            method,
            endpoint: url,
            url: fullUrl,
            $current_url: fullUrl,  // Use the actual API URL instead of page URL
            screen: window.location.pathname,
            statusCode,
            duration: Math.round(duration),
            success: response.ok,
          },
        }).catch(() => {
          // Silently fail if telemetry has issues
        });
      }

      return response;
    } catch (error) {
      const duration = performance.now() - startTime;

      // 📊 Track failed API calls
      if (telemetryService.isInitialized()) {
        telemetryService.capture(`api_call_error_${method.toLowerCase()}`, 'api-interceptor', {
          properties: {
            method,
            endpoint: url,
            url: fullUrl,
            $current_url: fullUrl,  // Use the actual API URL instead of page URL
            screen: window.location.pathname,
            duration: Math.round(duration),
            error: error instanceof Error ? error.message : 'Unknown error',
          },
        }).catch(() => {
          // Silently fail if telemetry has issues
        });
      }

      throw error;
    }
  }

  // Request APIs
  async submitRequest(request: Omit<TeamRequest, "id" | "status" | "submittedDate" | "azureTenantId">, teamId?: string): Promise<ApiResponse<TeamRequest>> {
    // Tente de récupérer le token utilisateur Teams pour OBO et l'envoyer au backend
    const userToken = await getTeamsUserToken();
    const response = await this.fetchWithAuth("/requests", {
      method: "POST",
      headers: userToken ? { Authorization: `Bearer ${userToken}` } : undefined,
      body: JSON.stringify({ ...request, teamId }),
    });

    return response.json();
  }

  async getMyRequests(userId: string): Promise<ApiResponse<TeamRequest[]>> {
    const response = await this.fetchWithAuth(`/requests?userId=${userId}`);
    return response.json();
  }

  async getAllRequests(): Promise<ApiResponse<TeamRequest[]>> {
    const response = await this.fetchWithAuth("/requests?isAdmin=true");
    return response.json();
  }

  async getRequestById(requestId: string): Promise<ApiResponse<TeamRequest>> {
    const response = await this.fetchWithAuth(`/requests/${requestId}`);
    return response.json();
  }

  async approveRequest(requestId: string, approverId: string, approverEmail: string, comments?: string): Promise<ApiResponse<TeamRequest>> {
    const response = await this.fetchWithAuth(`/requests/${requestId}/approve`, {
      method: "POST",
      body: JSON.stringify({ approverId, approverEmail, comments }),
    });

    return response.json();
  }

  async rejectRequest(requestId: string, approverId: string, approverEmail: string, comments?: string): Promise<ApiResponse<TeamRequest>> {
    const response = await this.fetchWithAuth(`/requests/${requestId}/reject`, {
      method: "POST",
      body: JSON.stringify({ approverId, approverEmail, comments }),
    });

    return response.json();
  }

  async cancelRequest(requestId: string, userId: string): Promise<ApiResponse<TeamRequest>> {
    const response = await this.fetchWithAuth(`/requests/${requestId}/cancel`, {
      method: "POST",
      body: JSON.stringify({ userId }),
    });

    return response.json();
  }

  async retryRequest(requestId: string): Promise<ApiResponse> {
    const response = await this.fetchWithAuth(`/requests/${requestId}/retry`, {
      method: "POST",
    });

    return response.json();
  }

  async getStatistics(): Promise<ApiResponse> {
    const response = await this.fetchWithAuth("/requests/stats/summary");
    return response.json();
  }

  // Template APIs
  async getEnabledTemplates(): Promise<ApiResponse<TeamTemplate[]>> {
    const response = await this.fetchWithAuth("/templates");
    return response.json();
  }

  async getAllTemplates(): Promise<ApiResponse<TeamTemplate[]>> {
    const response = await this.fetchWithAuth("/templates/all");
    return response.json();
  }

  async getAllTeams(): Promise<ApiResponse> {
    const response = await this.fetchWithAuth("/templates/teams");
    return response.json();
  }

  async checkTeamName(teamName: string): Promise<ApiResponse<{ exists: boolean; teamName: string; team: any | null }>> {
    const encodedName = encodeURIComponent(teamName);
    const response = await this.fetchWithAuth(`/teams/check-name/${encodedName}`);
    return response.json();
  }

  async createTemplate(template: Omit<TeamTemplate, "id">): Promise<ApiResponse<TeamTemplate>> {
    const response = await this.fetchWithAuth("/templates", {
      method: "POST",
      body: JSON.stringify(template),
    });

    return response.json();
  }

  async updateTemplate(templateId: string, updates: Partial<TeamTemplate>): Promise<ApiResponse> {
    const response = await this.fetchWithAuth(`/templates/${templateId}`, {
      method: "PUT",
      body: JSON.stringify(updates),
    });

    return response.json();
  }

  // Admin APIs
  async getSettings(): Promise<ApiResponse<AppSettings>> {
    const userToken = await getTeamsUserToken();
    const response = await this.fetchWithAuth("/admin/settings", {
      headers: userToken ? { Authorization: `Bearer ${userToken}` } : undefined,
    });
    return response.json();
  }

  async updateSettings(settings: AppSettings): Promise<ApiResponse<AppSettings>> {
    const userToken = await getTeamsUserToken();
    const response = await this.fetchWithAuth("/admin/settings", {
      method: "PUT",
      headers: userToken ? { Authorization: `Bearer ${userToken}` } : undefined,
      body: JSON.stringify(settings),
    });

    return response.json();
  }

  async getTeamInfo(teamId: string): Promise<ApiResponse> {
    const response = await this.fetchWithAuth(`/team/info?teamId=${teamId}`);
    return response.json();
  }

  async getTenantInfo(tenantId: string): Promise<ApiResponse> {
    const response = await this.fetchWithAuth(`/tenants/${tenantId}`);
    return response.json();
  }

  // User APIs
  async searchUsers(searchTerm: string): Promise<ApiResponse> {
    const response = await this.fetchWithAuth(`/users/search?q=${encodeURIComponent(searchTerm)}`);
    return response.json();
  }

  // Security Groups APIs
  async searchSecurityGroups(searchTerm: string): Promise<ApiResponse> {
    const response = await this.fetchWithAuth(`/groups/security-search?q=${encodeURIComponent(searchTerm)}`);
    return response.json();
  }

  async getUserById(userId: string): Promise<ApiResponse> {
    const response = await this.fetchWithAuth(`/users/${userId}`);
    return response.json();
  }

  // Adaptive Card Custom APIs (for approval actions)
  async processApprovalAction(action: {
    type: "approve" | "reject";
    requestId: string;
    approverId: string;
    approverEmail: string;
    comments?: string;
  }): Promise<ApiResponse> {
    const response = await this.fetchWithAuth("/adaptivecard-custom/action", {
      method: "POST",
      body: JSON.stringify(action),
    });

    return response.json();
  }

  async getRequests(): Promise<ApiResponse<TeamRequest[]>> {
    const response = await this.fetchWithAuth("/requests");
    return response.json();
  }

  // ==================== ADMINISTRATORS APIs ====================

  async getAllAdministrators(): Promise<ApiResponse<Administrator[]>> {
    const userToken = await getTeamsUserToken();
    const response = await this.fetchWithAuth("/admin/administrators", {
      headers: userToken ? { Authorization: `Bearer ${userToken}` } : undefined,
    });
    return response.json();
  }

  async addAdministrator(admin: Administrator): Promise<ApiResponse<Administrator>> {
    const userToken = await getTeamsUserToken();
    const response = await this.fetchWithAuth("/admin/administrators", {
      method: "POST",
      headers: userToken ? { Authorization: `Bearer ${userToken}` } : undefined,
      body: JSON.stringify(admin),
    });
    return response.json();
  }

  async removeAdministrator(adminId: string): Promise<ApiResponse> {
    const userToken = await getTeamsUserToken();
    const response = await this.fetchWithAuth(`/admin/administrators/${adminId}`, {
      method: "DELETE",
      headers: userToken ? { Authorization: `Bearer ${userToken}` } : undefined,
    });
    return response.json();
  }

  async checkIsAdministrator(userId: string): Promise<ApiResponse<{ isAdministrator: boolean }>> {
    const userToken = await getTeamsUserToken();
    const response = await this.fetchWithAuth(`/admin/administrators/check/${userId}`, {
      headers: userToken ? { Authorization: `Bearer ${userToken}` } : undefined,
    });
    return response.json();
  }

  // Team Privacy Types APIs
  async getAllTeamPrivacyTypes(): Promise<ApiResponse<TeamPrivacyType[]>> {
    const response = await this.fetchWithAuth("/team-types");
    return response.json();
  }

  async getEnabledTeamPrivacyTypes(): Promise<ApiResponse<TeamPrivacyType[]>> {
    const response = await this.fetchWithAuth("/team-types/enabled");
    return response.json();
  }

  async getTeamPrivacyTypeById(id: string): Promise<ApiResponse<TeamPrivacyType>> {
    const response = await this.fetchWithAuth(`/team-types/${id}`);
    return response.json();
  }

  // Security Groups APIs
  async getAllowedSecurityGroup(): Promise<ApiResponse<GraphUser | null>> {
    const userToken = await getTeamsUserToken();
    const response = await this.fetchWithAuth("/admin/allowed-security-group", {
      headers: userToken ? { Authorization: `Bearer ${userToken}` } : undefined,
    });
    return response.json();
  }

  async setAllowedSecurityGroup(group: GraphUser): Promise<ApiResponse<GraphUser>> {
    const userToken = await getTeamsUserToken();
    const response = await this.fetchWithAuth("/admin/allowed-security-group", {
      method: "POST",
      headers: userToken ? { Authorization: `Bearer ${userToken}` } : undefined,
      body: JSON.stringify(group),
    });
    return response.json();
  }

  async removeAllowedSecurityGroup(): Promise<ApiResponse> {
    const userToken = await getTeamsUserToken();
    const response = await this.fetchWithAuth("/admin/allowed-security-group", {
      method: "DELETE",
      headers: userToken ? { Authorization: `Bearer ${userToken}` } : undefined,
    });
    return response.json();
  }

  // Setup/Configuration APIs
  async getProvisioningStatus(): Promise<ApiResponse<any>> {
    const response = await this.fetchWithAuth("/setup/status");
    return response.json();
  }

  // ✨ MULTI-TENANT: Tenant registration API
  async registerTenant(params: {
    azureTenantId: string;
    createdBy?: string;
    createdByEmail?: string;
  }): Promise<ApiResponse<any>> {
    // ✅ CROSS-TENANT: Include user token for OBO flow to access client tenant organization info
    const userToken = await getTeamsUserToken();
    const response = await this.fetchWithAuth("/tenants/register", {
      method: "POST",
      headers: userToken ? { Authorization: `Bearer ${userToken}` } : undefined,
      body: JSON.stringify(params),
    });
    return response.json();
  }

  // ✨ MULTI-TENANT: Refresh tenant info after permission consent
  async refreshTenantInfo(azureTenantId: string): Promise<ApiResponse<any>> {
    console.log("[apiService] 🔄 Refreshing tenant info...", { azureTenantId });

    // ✅ CROSS-TENANT: Include user token for OBO flow to access client tenant organization info
    const userToken = await getTeamsUserToken();

    if (!userToken) {
      console.error("[apiService] ❌ Cannot refresh tenant info: No user token available");
      return {
        success: false,
        error: "No user token available for OBO flow"
      };
    }

    console.log("[apiService] ✅ User token available, calling refresh-info endpoint...");

    const response = await this.fetchWithAuth(`/tenants/${azureTenantId}/refresh-info`, {
      method: "POST",
      headers: { Authorization: `Bearer ${userToken}` },
    });

    const result = await response.json();

    if (result.success) {
      console.log("[apiService] ✅ Tenant info refreshed successfully:", result.tenant);
    } else {
      console.error("[apiService] ❌ Failed to refresh tenant info:", result.error, result.message);
    }

    return result;
  }

  // ==================== SUBSCRIPTION APIs ====================

  async getUsageStats(): Promise<ApiResponse<any>> {
    const response = await this.fetchWithAuth("/subscription/usage");
    return response.json();
  }

  async activateLicense(licenseKey: string): Promise<ApiResponse<any>> {
    const response = await this.fetchWithAuth("/subscription/activate-license", {
      method: "POST",
      body: JSON.stringify({ licenseKey }),
    });
    return response.json();
  }

  async getSubscriptionPlans(): Promise<ApiResponse<any[]>> {
    const response = await this.fetchWithAuth("/subscription/plans");
    return response.json();
  }

  async getMarketplaceUrl(): Promise<ApiResponse<{ url: string; offerId: string }>> {
    const response = await this.fetchWithAuth("/subscription/marketplace-url");
    return response.json();
  }

  // ✨ FREEMIUM: Get current tenant info (license key, subscription details)
  async getCurrentTenantInfo(): Promise<ApiResponse<any>> {
    const response = await this.fetchWithAuth("/tenants/current");
    return response.json();
  }

  // ✨ FREEMIUM: Unsubscribe from marketplace subscription
  // ✅ SECURITY: Call secure server-side endpoint that retrieves subscription ID from tenants table
  async unsubscribeFromMarketplace(): Promise<ApiResponse<any>> {
    const userToken = await getTeamsUserToken();
    const response = await this.fetchWithAuth("/subscription/unsubscribe", {
      method: "POST",
      headers: userToken ? { Authorization: `Bearer ${userToken}` } : undefined,
      body: JSON.stringify({})
    });
    return response.json();
  }
}

export const apiService = new ApiService();
